/* Northern Atelier – post add-to-cart upsell pop-up (see sections/sa-upsell-popup.liquid) */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const root = () => window.Shopify?.routes?.root || '/';
const OFFER_PROP = '_na_upsell';
const storage = {
  get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } },
  set(k, v) { try { localStorage.setItem(k, v); } catch (e) {} },
};
const getCart = () => fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
const drawerSections = () =>
  [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean).join(',');

/* Shopify money format ("${{amount}}", "{{amount_with_comma_separator}} €", …) */
function formatMoney(cents, format) {
  const fmt = format || '${{amount}}';
  const split = (n, dec, t, d) => {
    const [i, f] = (n / 100).toFixed(dec).split('.');
    return i.replace(/\B(?=(\d{3})+(?!\d))/g, t) + (f ? d + f : '');
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals': return split(cents, 0, ',', '.');
      case 'amount_with_comma_separator': return split(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return split(cents, 0, '.', ',');
      case 'amount_with_apostrophe_separator': return split(cents, 2, "'", '.');
      default: return split(cents, 2, ',', '.');
    }
  });
}

class SaUpsellPopup extends HTMLElement {
  connectedCallback() {
    // offer items only stay in the cart (and keep the code) while a full-price item is in it
    this.onAnyCartUpdate = (event) => {
      event.promise?.then((res) => {
        if (res?.detail?.didError || res?.detail?.source === 'sa-upsell-guard') return;
        this.enforce();
      }).catch(() => {});
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onAnyCartUpdate);
    this.enforce();

    this.dialog = this.querySelector('dialog');
    this.cards = [...this.querySelectorAll('[data-card]')];
    if (!this.dialog || !this.cards.length) return;
    this.minutes = Number(this.dataset.minutes) || 3;
    this.pct = Number(this.dataset.pct) || 0;
    this.expiryKey = `naUpsell:${this.dataset.key || 'popup'}:expiresAt`;

    this.querySelectorAll('[data-close]').forEach((b) => b.addEventListener('click', () => this.close()));
    this.dialog.addEventListener('close', () => clearInterval(this.timer));
    this.querySelector('[data-checkout]')?.addEventListener('click', () => { window.location.href = root() + 'checkout'; });
    this.cards.forEach((card) => {
      card.querySelector('[data-add]')?.addEventListener('click', () => this.openQuickView(card));
      card.querySelectorAll('[data-open]').forEach((a) => a.addEventListener('click', (e) => {
        e.preventDefault();
        this.openQuickView(card);
      }));
    });
    this.grid = this.querySelector('[data-view-grid]');
    this.qv = this.querySelector('[data-qv]');
    this.querySelector('[data-qv-back]')?.addEventListener('click', () => this.showGrid());
    this.qvAdd = this.querySelector('[data-qv-add]');
    this.checkoutBtn = this.querySelector('[data-checkout]');
    this.qvAdd?.addEventListener('click', () => this.addFromQuickView());

    this.onCartUpdate = (event) => {
      event.promise?.then((res) => {
        const d = res?.detail;
        // only real add-to-carts from the theme, never our own adds
        if (!d || d.didError || d.source !== 'sa') return;
        this.maybeOpen(d.items);
      }).catch(() => {});
    };
    document.addEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);

    if (this.hasAttribute('data-design-mode')) {
      const mine = (e) => e.target instanceof Element && (e.target.contains(this) || this.contains(e.target));
      document.addEventListener('shopify:section:select', (e) => { if (mine(e)) this.open(true); });
      document.addEventListener('shopify:section:deselect', (e) => { if (mine(e)) this.close(); });
    }
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onCartUpdate);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.onAnyCartUpdate);
    clearInterval(this.timer);
  }

  async maybeOpen(items) {
    if (this.dialog.open) return;
    const expiresAt = Number(storage.get(this.expiryKey));
    if (expiresAt && Date.now() >= expiresAt) return; // offer already used up its time
    if (!items) {
      try { items = (await getCart()).items || []; } catch (e) { return; }
    }
    if (items.some((i) => i.properties && i.properties[OFFER_PROP])) return; // one offer item per order
    if (!expiresAt) storage.set(this.expiryKey, String(Date.now() + this.minutes * 60000));
    this.reset();
    setTimeout(() => this.open(false), 400);
  }

  open(preview) {
    if (this.dialog.open) return;
    this.showGrid();
    this.dialog.showModal();
    this.startTimer(preview);
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  startTimer(preview) {
    clearInterval(this.timer);
    const el = this.querySelector('[data-timer]');
    const end = preview ? Date.now() + this.minutes * 60000 : Number(storage.get(this.expiryKey));
    const tick = () => {
      const left = Math.max(0, end - Date.now());
      const m = Math.floor(left / 60000);
      const s = Math.floor((left % 60000) / 1000);
      if (el) el.textContent = `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
      // never close on its own while the customer is choosing; once expired it just won't open again
      if (left <= 0) clearInterval(this.timer);
    };
    tick();
    if (!preview) this.timer = setInterval(tick, 1000);
  }

  reset() {
    this.cards.forEach((card) => {
      const btn = card.querySelector('[data-add]');
      if (btn.dataset.label) btn.textContent = btn.dataset.label;
      btn.classList.remove('is-added');
      btn.disabled = false;
      const err = card.querySelector('[data-error]');
      if (err) err.hidden = true;
    });
  }

  money(cents) {
    return formatMoney(cents, this.dataset.moneyFormat);
  }

  showGrid() {
    if (this.qv) this.qv.hidden = true;
    if (this.grid) this.grid.hidden = false;
    if (this.qvAdd) this.qvAdd.hidden = true;
    if (this.checkoutBtn) this.checkoutBtn.hidden = false;
    this.current = null;
  }

  /* ---------- quick view: colour (linked to the image) + size ---------- */
  openQuickView(card) {
    let data;
    try { data = JSON.parse(card.querySelector('script[data-json]').textContent); } catch (e) { return; }
    const start = data.variants.find((v) => v.id === data.selected) || data.variants.find((v) => v.available) || data.variants[0];
    this.current = { card, data, selected: [...(start?.options || [])] };
    // colour first, then the other options in their natural order
    const n = data.options.length;
    this.current.order = data.colorIndex >= 0
      ? [data.colorIndex, ...[...Array(n).keys()].filter((i) => i !== data.colorIndex)]
      : [...Array(n).keys()];

    this.qv.querySelector('[data-qv-type]').textContent = data.type || '';
    this.qv.querySelector('[data-qv-name]').textContent = data.title;
    const btn = this.qvAdd;
    btn.textContent = btn.dataset.label || (btn.dataset.label = btn.textContent);
    btn.classList.remove('is-added');
    btn.hidden = false;
    if (this.checkoutBtn) this.checkoutBtn.hidden = true;
    this.qv.querySelector('[data-qv-error]').hidden = true;
    this.renderOptions();
    this.grid.hidden = true;
    this.qv.hidden = false;
    this.dialog.scrollTop = 0;
  }

  /* is there an available variant with `value` at `index`, given the options chosen before it? */
  isAvailable(index, value) {
    const { data, selected, order } = this.current;
    const before = order.slice(0, order.indexOf(index));
    return data.variants.some((v) => v.available && v.options[index] === value && before.every((j) => v.options[j] === selected[j]));
  }

  colorThumb(value) {
    const { data } = this.current;
    const v = data.variants.find((x) => x.options[data.colorIndex] === value && x.thumb);
    return v ? v.thumb : null;
  }

  renderOptions() {
    const { data, selected } = this.current;
    const wrap = this.qv.querySelector('[data-qv-options]');
    wrap.innerHTML = '';
    const single = data.options.length === 1 && data.options[0].values.length === 1 && data.options[0].values[0] === 'Default Title';
    if (!single) {
      data.options.forEach((opt, i) => {
        const group = document.createElement('div');
        group.className = 'sa-qv__opt';
        const label = document.createElement('p');
        label.className = 'sa-qv__label';
        label.append(opt.name + ': ');
        const strong = document.createElement('strong');
        strong.textContent = selected[i] || '';
        label.append(strong);
        const values = document.createElement('div');
        values.className = 'sa-qv__values';
        opt.values.forEach((value) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'sa-qv__val';
          b.setAttribute('aria-pressed', String(selected[i] === value));
          b.setAttribute('aria-label', `${opt.name}: ${value}`);
          if (!this.isAvailable(i, value)) b.classList.add('is-unavailable');
          const thumb = i === data.colorIndex ? this.colorThumb(value) : null;
          if (thumb) {
            b.classList.add('sa-qv__val--img');
            b.title = value;
            const img = document.createElement('img');
            img.src = thumb;
            img.alt = value;
            img.loading = 'lazy';
            b.append(img);
          } else {
            b.textContent = value;
          }
          b.addEventListener('click', () => this.select(i, value));
          values.append(b);
        });
        group.append(label, values);
        wrap.append(group);
      });
    }
    this.updateQuickView();
  }

  select(index, value) {
    const c = this.current;
    c.selected[index] = value;
    // keep the choice valid: if this combination does not exist / is sold out, jump to the first available one
    const exact = c.data.variants.find((v) => v.options.every((o, i) => o === c.selected[i]));
    if (!exact || !exact.available) {
      const upTo = c.order.slice(0, c.order.indexOf(index) + 1);
      const alt = c.data.variants.find((v) => v.available && upTo.every((j) => v.options[j] === c.selected[j]));
      if (alt) c.selected = [...alt.options];
    }
    this.renderOptions();
  }

  updateQuickView() {
    const { data, selected } = this.current;
    const v = data.variants.find((x) => x.options.every((o, i) => o === selected[i]));
    this.current.variant = v;
    const img = this.qv.querySelector('[data-qv-img]');
    const colorImg = data.colorIndex >= 0
      ? data.variants.find((x) => x.options[data.colorIndex] === selected[data.colorIndex] && x.image)?.image
      : null;
    const src = (v && v.image) || colorImg || data.image;
    if (src && img.getAttribute('src') !== src) img.src = src;
    img.alt = data.title + (v ? ' – ' + v.options.join(' / ') : '');
    // offer products carry their own reduced price (compare-at = full price); otherwise show the % off
    const hasCompare = v && v.compare > v.price;
    const cents = v ? (hasCompare ? v.compare : v.price) : 0;
    const now = v ? (hasCompare ? v.price : Math.round((cents * (100 - this.pct)) / 100)) : 0;
    this.qv.querySelector('[data-qv-old]').textContent = this.money(cents);
    this.qv.querySelector('[data-qv-new]').textContent = this.money(now);
    this.qv.querySelector('[data-qv-save]').textContent = '-' + this.money(cents - now);
    const btn = this.qvAdd;
    btn.disabled = !v || !v.available;
    if (!btn.classList.contains('is-added')) {
      btn.textContent = v && !v.available ? (this.dataset.soldOutLabel || 'Sold out') : btn.dataset.label;
    }
  }

  async addFromQuickView() {
    const c = this.current;
    const btn = this.qvAdd;
    if (!c || !c.variant || !c.variant.available || btn.disabled) return;
    btn.disabled = true;
    const err = this.qv.querySelector('[data-qv-error]');
    err.hidden = true;
    try {
      await this.addVariant(c.variant.id);
      btn.textContent = this.dataset.addedLabel || 'Added';
      btn.classList.add('is-added');
      const cardBtn = c.card.querySelector('[data-add]');
      cardBtn.dataset.label = cardBtn.dataset.label || cardBtn.textContent;
      cardBtn.textContent = this.dataset.addedLabel || 'Added';
      cardBtn.classList.add('is-added');
      setTimeout(() => { if (this.current === c) this.showGrid(); }, 900);
    } catch (e) {
      btn.disabled = false;
      err.textContent = e.message;
      err.hidden = false;
    }
  }

  /* No full-price item left → remove the offer items; no offer item left → drop our code. */
  async enforce() {
    if (this.enforcing) return;
    this.enforcing = true;
    try {
      const cart = await getCart();
      const offerIds = new Set([...this.querySelectorAll('[data-card]')].map((c) => Number(c.dataset.productId)));
      const isOffer = (i) => (i.properties && i.properties[OFFER_PROP]) || offerIds.has(i.product_id);
      const offer = cart.items.filter(isOffer);
      const main = cart.items.filter((i) => !isOffer(i));
      const code = (this.dataset.code || '').toUpperCase();
      const codes = (cart.discount_codes || []).map((d) => d.code);
      const hasCode = !!code && codes.some((c) => c.toUpperCase() === code);
      const dropOffer = offer.length > 0 && main.length === 0;
      if (!dropOffer && (offer.length || !hasCode)) return;

      const body = { sections: drawerSections() };
      if (dropOffer) body.updates = Object.fromEntries(offer.map((i) => [i.key, 0]));
      if (hasCode) body.discount = codes.filter((c) => c.toUpperCase() !== code).join(',');
      const deferred = CartLinesUpdateEvent.createPromise();
      document.dispatchEvent(new CartLinesUpdateEvent({
        action: 'remove',
        context: 'cart',
        lines: offer.map((i) => ({ id: i.key, quantity: 0 })),
        promise: deferred.promise,
      }));
      try {
        const res = await fetch(root() + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        const fresh = await getCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(fresh),
          detail: { items: fresh.items, source: 'sa-upsell-guard', sections: data.sections, didError: false },
        });
        document.querySelectorAll('[data-sa-cart-count]').forEach((el) => { el.textContent = fresh.item_count; el.hidden = !fresh.item_count; });
        if (dropOffer && /\/cart\/?$/.test(location.pathname)) location.reload();
      } catch (e) {
        deferred.resolve({ detail: { didError: true, source: 'sa-upsell-guard' } });
      }
    } catch (e) {
      // ignore – the next cart update will check again
    } finally {
      this.enforcing = false;
    }
  }

  async applyCode(sections) {
    const code = this.dataset.code;
    if (!code) return null;
    try {
      const r = await fetch(root() + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code, sections }),
      });
      if (r.ok) return await r.json();
    } catch (e) {}
    try { await fetch(root() + 'discount/' + encodeURIComponent(code), { credentials: 'same-origin' }); } catch (e) {}
    return null;
  }

  async addVariant(id) {
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(id), quantity: 1 }],
      promise: deferred.promise,
    }));
    const sections = drawerSections();
    try {
      const res = await fetch(root() + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id, quantity: 1, properties: { [OFFER_PROP]: '1' } }], sections }),
      });
      const data = await res.json();
      if (data.status) throw new Error(data.description || data.message || 'Error');
      let sectionsHtml = data.sections;
      const applied = await this.applyCode(sections);
      if (applied?.sections) sectionsHtml = applied.sections;
      const cart = await getCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'sa-upsell', itemCount: 1, sections: sectionsHtml, didError: false },
      });
      document.querySelectorAll('[data-sa-cart-count]').forEach((el) => { el.textContent = cart.item_count; el.hidden = !cart.item_count; });
    } catch (err) {
      deferred.resolve({ detail: { didError: true } });
      throw err;
    }
  }
}

if (!customElements.get('sa-upsell-popup')) customElements.define('sa-upsell-popup', SaUpsellPopup);
